'use client'

import { track, type ShareChannel, type ShareLoop, type ShareUnit } from '@/lib/analytics/mycio'

/**
 * Consumer growth-loops — referral attribution + share mechanics (consumer-loops-playbook.md).
 *
 * k-factor is computed IN PostHog from the loop events, so attribution here is client-side and needs NO new
 * DB table: a share link carries ?ref=<referrer distinct_id>&loop=<loop>; the invitee's landing stores that,
 * fires invite_clicked, and tags invitee_ran_instant / invitee_claimed so PostHog can join sender→invitee.
 * (Server-side reward attribution + fraud guard are the deliberately-deferred phase 2.)
 *
 * COMPLIANCE: shareable text is band-only, NEVER user dollar amounts; no incentive is claimed until the
 * reward engine ships (so no material-connection disclosure is needed yet). The user is always the sender —
 * this only builds links + opens the native share sheet / mail client; it never messages a third party.
 */

const REF_KEY = 'mycio_referral'
const TOKEN_KEY = 'mycio_ref_token'   // the current user's SIGNED referral token (C1)
const DEVICE_KEY = 'mycio_device_id'  // stable per-device id for the anti-farming cap
const REF_TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days — attribution window

type StoredReferral = { ref: string; loop: ShareLoop; at: number }

/** The caller's cached signed referral token (minted server-side; unforgeable). Null for anonymous users. */
export function cachedReferralToken(): string | null {
  try {
    return typeof window === 'undefined' ? null : localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

/** Fetch + cache the signed token for a logged-in user (C1). Anonymous users get none → their shares still
 *  drive measurement (PostHog events) but carry no reward attribution. Call on sign-in / share-surface mount. */
export async function primeReferralToken(accessToken?: string | null): Promise<void> {
  if (typeof window === 'undefined' || !accessToken) return
  try {
    const res = await fetch('/api/mycio/referrals/token', { headers: { Authorization: `Bearer ${accessToken}` } })
    const j = (await res.json().catch(() => ({}))) as { token?: string }
    if (j?.token) localStorage.setItem(TOKEN_KEY, j.token)
  } catch {
    /* offline / unauth — no token; shares just won't attribute a reward */
  }
}

/** Clear the token on sign-out so a different next user can't reuse it. */
export function clearReferralToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

/** A stable per-device id (anti-farming: a device can be a referee at most PER_DEVICE_CAP times). */
export function getDeviceId(): string | null {
  try {
    if (typeof window === 'undefined') return null
    let d = localStorage.getItem(DEVICE_KEY)
    if (!d) {
      d = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `d-${Date.now()}-${performance.now()}`
      localStorage.setItem(DEVICE_KEY, d)
    }
    return d
  } catch {
    return null
  }
}

/** The device id to hand the backend on claim (anti-farming cap). */
export function deviceIdForClaim(): string | null {
  return getDeviceId()
}

/** Build a shareable link carrying the sender's SIGNED referral token + which loop it came from. The token
 *  is absent for anonymous sharers → the link still works (measurement) but earns no reward attribution. */
export function buildShareUrl(loop: ShareLoop, targetPath = '/'): string {
  const base = process.env.NEXT_PUBLIC_FRONTEND_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://mycio.ai')
  const url = new URL(targetPath, base)
  const ref = cachedReferralToken()
  if (ref) url.searchParams.set('ref', ref)
  url.searchParams.set('loop', loop)
  return url.toString()
}

/**
 * Call once on landing (app-wide). If the URL carries ?ref, persist it (first-touch wins within the window)
 * and fire invite_clicked{loop}. Idempotent per stored referral so a refresh doesn't double-count.
 */
export function captureReferralOnLand(): void {
  if (typeof window === 'undefined') return
  try {
    const params = new URLSearchParams(window.location.search)
    const ref = params.get('ref')
    if (!ref) return
    const loop = (params.get('loop') as ShareLoop) || 'referral'
    const existing = getStoredReferral()
    // First-touch attribution: don't overwrite a live referral (avoids self-attribution on internal nav).
    if (existing && existing.ref === ref) return
    if (!existing) {
      const rec: StoredReferral = { ref, loop, at: Date.now() }
      localStorage.setItem(REF_KEY, JSON.stringify(rec))
      track('invite_clicked', { loop })
    }
  } catch {
    /* localStorage / URL unavailable — non-fatal */
  }
}

/** The stored referral if still within the attribution window, else null (and clears an expired one). */
export function getStoredReferral(): StoredReferral | null {
  try {
    const raw = localStorage.getItem(REF_KEY)
    if (!raw) return null
    const rec = JSON.parse(raw) as StoredReferral
    if (!rec?.ref || typeof rec.at !== 'number' || Date.now() - rec.at > REF_TTL_MS) {
      localStorage.removeItem(REF_KEY)
      return null
    }
    return rec
  } catch {
    return null
  }
}

/** Fire an invitee milestone (ran instant / claimed) only when this session was referred. */
export function trackInviteeMilestone(event: 'invitee_ran_instant' | 'invitee_claimed'): void {
  if (!getStoredReferral()) return
  track(event, {})
}

/** The referrer id to hand the backend on claim (best-effort — future reward attribution). */
export function referrerForClaim(): string | null {
  return getStoredReferral()?.ref ?? null
}

/** The loop the referral came from, so the backend can record per-loop attribution (else the column is null). */
export function referralLoopForClaim(): ShareLoop | null {
  return getStoredReferral()?.loop ?? null
}

/**
 * Open the native share sheet (mobile) or fall back to mailto / clipboard, and emit share_initiated/sent.
 * `text` must already be band-only + compliant. Returns the channel used.
 */
export async function shareOrFallback(opts: {
  loop: ShareLoop
  unit: ShareUnit
  title: string
  text: string
  url: string
  subject?: string
}): Promise<ShareChannel | null> {
  const canWebShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'
  const channel: ShareChannel = canWebShare ? 'web_share' : 'copy_link'
  track('share_initiated', { loop: opts.loop, unit: opts.unit, channel })
  try {
    if (canWebShare) {
      await navigator.share({ title: opts.title, text: opts.text, url: opts.url })
    } else {
      await navigator.clipboard.writeText(`${opts.text} ${opts.url}`)
    }
    track('share_sent', { channel })
    return channel
  } catch {
    return null // user cancelled / blocked
  }
}

/** Build a mailto: link (desktop fallback / explicit email channel). */
export function mailtoLink(subject: string, body: string, url: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${body}\n\n${url}`)}`
}
